import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";

const fitFactors = [
  "budget you can sustain",
  "healthcare you can rely on",
  "a residency path that looks realistic",
  "infrastructure and daily convenience",
  "lifestyle fit",
  "the tradeoffs you can actually live with",
];

const commonMistakes = [
  "treating cost alone like the whole decision",
  "assuming the most popular country is the best fit",
  "confusing a good vacation destination with a workable long-term base",
  "comparing countries without first deciding what matters most",
];

const exampleProfiles = [
  {
    title: "If budget and healthcare come first",
    copy: "Spain, Portugal, and Italy may all stay in the conversation, but for different reasons. Spain may offer stronger healthcare confidence. Portugal may feel easier to manage day to day. Italy may still appeal if lifestyle fit matters enough to accept more variation.",
  },
  {
    title: "If residency practicality matters most",
    copy: "Panama, Portugal, or Costa Rica may look stronger than countries that are easy to visit but harder to stay in. The key question is not whether the country is appealing. It is whether the move actually looks workable long term.",
  },
  {
    title: "If infrastructure and big-city access matter more",
    copy: "Germany, the Netherlands, and France may rise because daily systems, transport, and city access hold up differently there than they do in slower, lower-cost options.",
  },
];

const renderBulletList = (items, prefix) => (
  <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600" data-testid={`${prefix}-list`}>
    {items.map((item, index) => (
      <li key={item} className="flex gap-3" data-testid={`${prefix}-item-${index + 1}`}>
        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function WhatCountryShouldIMoveTo() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="what-country-should-i-move-to-page">
      <SeoArticleSchema
        headline="What Country Should I Move To?"
        description="Choosing a country to move to depends on your budget, healthcare needs, residency path, lifestyle, and tradeoffs. Learn how to compare countries more clearly before you commit."
        pathname="/insights/what-country-should-i-move-to"
        keywords={[
          "what country should i move to",
          "best country to move abroad",
          "how to choose a country abroad",
          "best country for expats",
          "where should i retire abroad",
        ]}
      />
      <Header />

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-10 shadow-[0_18px_48px_rgba(28,25,23,0.06)] sm:px-10" data-testid="what-country-hero-section">
            <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="what-country-hero-accent" />
            <p className="rc-section-kicker mb-4" data-testid="what-country-hero-kicker">Decision guide</p>
            <h1 className="font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="what-country-page-title">
              What Country Should I Move To?
            </h1>
            <div className="mt-6 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="what-country-page-intro">
              <p className="text-stone-900">There isn’t one best country to move to.</p>
              <p>
                The right country depends on how well it fits your budget, healthcare expectations, residency pathway, lifestyle preferences, and tolerance for tradeoffs.
              </p>
              <p>
                Most people do not get stuck because there are no good options. They get stuck because several countries look good for different reasons.
              </p>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="what-country-key-takeaway"
            paragraphs={[
              "There is no universally best country to move abroad. The right answer depends on how well a country matches your budget, healthcare needs, residency path, lifestyle priorities, and tolerance for tradeoffs.",
              "Different rankings disagree because they measure different things. A stronger relocation decision comes from knowing what should matter most for your own life before you compare destinations side by side.",
            ]}
          />

          <section className="grid gap-6 lg:grid-cols-[1.05fr_1.2fr]" data-testid="what-country-short-answer-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="what-country-short-answer-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="what-country-short-answer-title">What is the short answer?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="what-country-short-answer-copy">
                <p>If you are asking what country should I move to, start by comparing fit instead of popularity.</p>
                <p>Country fit usually comes down to a few practical questions: Can you afford it? Can you stay there? Will healthcare feel reliable enough? Will daily life actually suit you?</p>
                <p className="text-stone-800">The calmer question is not “what is the best country overall?” It is “which countries make the most sense for my situation?”</p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="what-country-why-hard-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="what-country-why-hard-title">Why do “best country” answers conflict so often?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="what-country-why-hard-copy">
                <p>At first it sounds simple: somewhere cheaper, safer, healthier, and easier.</p>
                <p>Then the tradeoffs show up. Lower cost can come with weaker healthcare. Easier residency can come with less long-term stability. A slower pace can come with more variable infrastructure.</p>
                <p>This is where broad country hype stops helping.</p>
              </div>
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="what-country-fit-factors-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="what-country-fit-factors-title">What should matter most when choosing a country?</h2>
            <p className="mt-5 max-w-4xl text-base leading-8 text-stone-600" data-testid="what-country-fit-factors-copy-1">
              A stronger answer usually comes from six practical layers working together:
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="what-country-fit-factors-grid">
              {fitFactors.map((item, index) => (
                <div key={item} className="rounded-[1.35rem] border border-stone-200/80 bg-stone-50/75 px-4 py-3" data-testid={`what-country-fit-factor-${index + 1}`}>
                  <p className="text-sm leading-7 text-stone-700">{item}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 max-w-4xl space-y-4 text-base leading-8 text-stone-600">
              <p>Budget matters because a move only works if you can sustain it. Healthcare matters because peace of mind is different from a good headline ranking.</p>
              <p>Residency practicality matters because a country can feel easy to visit while still being harder to stay in. Infrastructure and lifestyle fit matter because day-to-day life is where the move becomes real.</p>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="what-country-comparison-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="what-country-comparison-examples-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="what-country-comparison-examples-title">Why can two people get different answers from the same shortlist?</h2>
              <div className="mt-5 space-y-5" data-testid="what-country-comparison-examples-list">
                {exampleProfiles.map((example, index) => (
                  <div key={example.title} className="rounded-[1.35rem] border border-stone-200/80 bg-stone-50/80 p-5" data-testid={`what-country-comparison-example-${index + 1}`}>
                    <h3 className="font-manrope text-lg text-stone-900">{example.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-stone-600">{example.copy}</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="what-country-comparison-examples-copy-2">
                If you are already comparing two realistic options, a page like{" "}
                <Link
                  to="/insights/portugal-vs-spain-living-abroad"
                  className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                  data-testid="what-country-portugal-spain-link"
                >
                  Portugal vs Spain: Which Country Fits Your Priorities?
                </Link>
                {" "}can help clarify the tradeoffs.
              </p>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="what-country-common-mistakes-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="what-country-common-mistakes-title">What mistakes make this decision harder than it needs to be?</h2>
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="what-country-common-mistakes-copy-1">
                Most early mistakes look reasonable at first. They just make the decision harder later.
              </p>
              {renderBulletList(commonMistakes, "what-country-common-mistakes")}
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="what-country-common-mistakes-copy-2">
                If you want to avoid the cost-only trap, start with{" "}
                <Link
                  to="/insights/why-cost-to-retire-abroad-charts-are-misleading"
                  className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                  data-testid="what-country-cost-chart-link"
                >
                  why cost charts do not tell the whole story
                </Link>
                .
              </p>
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="what-country-better-way-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="what-country-better-way-title">What is a better way to decide?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="what-country-better-way-copy">
              <p>Instead of asking “what country should I move to?”, ask which countries fit your situation and what tradeoffs come with each one.</p>
              <p>If you are still broad and early, move from discovery to structure first. A page like{" "}
                <Link
                  to="/insights/create-country-shortlist-for-moving-abroad"
                  className="font-medium text-stone-700 underline-offset-4 transition-colors duration-200 hover:text-stone-900 hover:underline"
                  data-testid="what-country-shortlist-link"
                >
                  how to create a country shortlist
                </Link>
                {" "}helps you narrow the field before you start comparing finer details.
              </p>
              <p className="text-stone-800">The goal is not to find a perfect country. The goal is to narrow your options, understand the tradeoffs, and make a clearer decision.</p>
            </div>
          </section>

          <ContextualLinkPanel
            kicker="A useful path from here"
            title="Move from the broad question into a narrower decision."
            description="If this page helped frame the problem, the next useful move is usually to narrow the field, look at a real comparison, and then preview how the Blueprint organizes the decision before you personalize it."
            testIdPrefix="what-country-next-steps"
            items={[
              {
                slug: "shortlist",
                eyebrow: "Narrow the field",
                title: "Build a realistic shortlist",
                description: "Use a clearer shortlist process before you spend more time comparing countries one by one.",
                ctaLabel: "Read the shortlist guide",
                to: "/insights/create-country-shortlist-for-moving-abroad",
              },
              {
                slug: "comparison",
                eyebrow: "See a comparison",
                title: "Compare Portugal and Spain",
                description: "See how a practical head-to-head comparison turns broad interest into clearer tradeoffs.",
                ctaLabel: "Open the comparison",
                to: "/insights/portugal-vs-spain-living-abroad",
              },
              {
                slug: "sample-blueprint",
                eyebrow: "See the output",
                title: "Preview a Sample Blueprint",
                description: "See how the paid experience turns a shortlist into a more structured recommendation and plan.",
                ctaLabel: "Preview the sample",
                to: "/sample-plan",
              },
            ]}
          />

          <section className="rounded-[2rem] border border-stone-600/70 bg-stone-900 px-7 py-9 text-stone-50 shadow-[0_18px_48px_rgba(28,25,23,0.12)] sm:px-10" data-testid="what-country-cta-section">
            <div className="mb-5 h-1.5 w-14 rounded-full bg-amber-400/85" data-testid="what-country-cta-accent" />
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-stone-300" data-testid="what-country-cta-kicker">Next step</p>
            <h2 className="mt-3 font-manrope text-3xl text-stone-50" data-testid="what-country-cta-title">Ready to move from broad interest to a real shortlist?</h2>
            <p className="mt-4 max-w-3xl text-base leading-8 text-stone-300" data-testid="what-country-cta-copy">
              The assessment turns broad country interest into a structured comparison across priorities, budget, residency practicality, and the tradeoffs that shape a workable move.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <Button asChild className="rounded-full bg-white px-6 text-stone-900 hover:bg-amber-100" data-testid="what-country-cta-start-assessment-button">
                <Link to="/auth">
                  Start your assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}